import { useEffect, useMemo, useState } from "react";

import {
  Loader2,
  Eye,
  EyeOff,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { makeRequest } from "@/utils/makeRequest";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { queryClient } from "@/services/queryClient";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { UserRole } from "@/interfaces/profile";
import {
  loginSuccess,
  setSecurityCodeVerified,
} from "@/store/slices/authSlice";
import { useProfile } from "@/hooks";
import Loader from "@/components/loader/Loader";

const calculateStrength = (value: string) => {
  let score = 0;

  if (value.length >= 8) score++;
  if (/[A-Z]/.test(value)) score++;
  if (/[a-z]/.test(value)) score++;
  if (/\d/.test(value)) score++;
  if (/[^A-Za-z0-9]/.test(value)) score++;

  return score;
};

const getStrengthLabel = (score: number) => {
  if (score <= 1) return "Very Weak";
  if (score === 2) return "Weak";
  if (score === 3) return "Fair";
  if (score === 4) return "Strong";
  return "Very Strong";
};

const getRequirements = (value: string) => [
  {
    label: "At least 8 characters",
    valid: value.length >= 8,
  },
  {
    label: "One uppercase letter",
    valid: /[A-Z]/.test(value),
  },
  {
    label: "One lowercase letter",
    valid: /[a-z]/.test(value),
  },
  {
    label: "One number",
    valid: /\d/.test(value),
  },
  {
    label: "One special character",
    valid: /[^A-Za-z0-9]/.test(value),
  },
];

const PasswordField = ({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) => {
  const [visible, setVisible] = useState(false);

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">{label}</label>

      <div className="relative">
        <input
          type={visible ? "text" : "password"}
          value={value}
          placeholder={placeholder}
          autoComplete="new-password"
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-14 rounded-xl border border-border bg-background px-4 pr-12 outline-none transition focus:ring-2 focus:ring-primary/30 focus:border-primary"
        />

        <button
          type="button"
          onClick={() => setVisible((current) => !current)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          aria-label={
            visible ? "Hide transaction password" : "Show transaction password"
          }
        >
          {visible ? (
            <EyeOff className="h-5 w-5" />
          ) : (
            <Eye className="h-5 w-5" />
          )}
        </button>
      </div>
    </div>
  );
};

export default function TransactionPasswordSetupPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { user, isLoading: isLoadingUser } = useAppSelector(
    (state) => state.auth,
  );

  const userId = user?.id ?? "";

  const {
    data: profile,
    isLoading,
    refetch: refetchProfile,
  } = useProfile(userId);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const strength = useMemo(() => calculateStrength(password), [password]);

  const requirements = useMemo(() => getRequirements(password), [password]);

  const strong = requirements.every((item) => item.valid);

  const passwordsMatch = password.length > 0 && password === confirmPassword;

  const canSubmit = strong && passwordsMatch && !loading;

  useEffect(() => {
    if (!user) {
      return;
    }

    if (profile?.isSecurityCodeVerified) {
      const roleRoute =
        user.role === UserRole.SUPER || user.role === UserRole.ADMIN
          ? "/admin"
          : "/dashboard";

      navigate(roleRoute, {
        replace: true,
      });
    }
  }, [user, profile, navigate]);

  const handleSubmit = async () => {
    if (!canSubmit || !userId || !user) {
      return;
    }

    try {
      setLoading(true);

      const response = await makeRequest.put("/user/security-code/create", {
        securityCode: password,
      });

      toast.success(
        response?.data?.message || "Transaction password created successfully.",
      );

      const refreshedProfile = await refetchProfile();

      if (!refreshedProfile.data?.isSecurityCodeVerified) {
        toast.error(
          "Transaction password was created, but your profile has not updated yet. Please try again.",
        );

        return;
      }

      dispatch(setSecurityCodeVerified(true));

      const roleRoute =
        user.role === UserRole.SUPER || user.role === UserRole.ADMIN
          ? "/admin"
          : "/dashboard";

      navigate(roleRoute, {
        replace: true,
      });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  if (!user || isLoading || isLoadingUser) {
    return <Loader type="bounce" />;
  }
  // if (!user) {
  //   return (
  //     <div className="min-h-screen bg-background flex items-center justify-center">
  //       <Loader2 className="h-8 w-8 animate-spin text-primary" />
  //     </div>
  //   );
  // }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 ring-1 ring-primary/20">
            <ShieldCheck className="h-9 w-9 text-primary" />
          </div>

          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            Secure your transactions
          </h1>

          <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
            Create a transaction password to protect withdrawals, transfers,
            swaps, and other sensitive account operations.
          </p>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-border/60 bg-card p-5 sm:p-7 shadow-xl shadow-black/5">
          {/* Security notice */}
          <div className="mb-6 rounded-xl border border-primary/20 bg-primary/5 p-4">
            <div className="flex gap-3">
              <ShieldCheck className="h-5 w-5 text-primary mt-0.5 shrink-0" />

              <div>
                <p className="text-sm font-semibold">Transaction password</p>

                <p className="mt-1 text-xs leading-5 text-muted-foreground">
                  This password is separate from your login password. You will
                  use it to authorize sensitive financial operations.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-5">
            {/* Transaction Password */}
            <PasswordField
              label="Transaction Password"
              value={password}
              onChange={setPassword}
              placeholder="Create a transaction password"
            />

            {/* Strength */}
            {password.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    Password strength
                  </span>

                  <span className="text-xs font-semibold">
                    {getStrengthLabel(strength)}
                  </span>
                </div>

                <Progress value={(strength / 5) * 100} className="h-2" />

                <div className="grid gap-2">
                  {requirements.map((requirement) => (
                    <div
                      key={requirement.label}
                      className="flex items-center gap-2 text-xs"
                    >
                      {requirement.valid ? (
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                      ) : (
                        <XCircle className="h-4 w-4 text-destructive" />
                      )}

                      <span
                        className={
                          requirement.valid
                            ? "text-foreground"
                            : "text-muted-foreground"
                        }
                      >
                        {requirement.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Confirmation */}
            {strong && (
              <PasswordField
                label="Confirm Transaction Password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                placeholder="Confirm your transaction password"
              />
            )}

            {confirmPassword && !passwordsMatch && (
              <p className="text-xs text-destructive">
                Transaction passwords do not match.
              </p>
            )}

            {/* Submit */}
            <Button
              type="button"
              disabled={!canSubmit}
              onClick={handleSubmit}
              className="w-full h-14 rounded-xl font-semibold"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Securing account...
                </>
              ) : (
                <>
                  Create Transaction Password
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>

            <p className="text-center text-[11px] leading-5 text-muted-foreground">
              Never share your transaction password with anyone, including
              platform staff or support personnel.
            </p>
          </div>
        </div>

        {/* Bottom security indicator */}
        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="h-4 w-4 text-emerald-500" />
          <span>Your transaction security is protected</span>
        </div>
      </div>
    </div>
  );
}
